import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useRefundsMain } from '../../hooks/useRefundsMain';
import { RefundCardList, RefundsAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, RefundsStatusToolbar } from '../interaction';

export const Main = () => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    selectedColumns,
    orders,
    isLoading,
    isFetching,
    error,
    total,
    statusCounts,
    isLoadingStatusCounts,
    ordersViewProps,
    searchTerm,
    statusFilter,
    dateFrom,
    dateTo,
    shopIdParam,
    handleSearch,
    handleStatusChange,
    handleDateRangeChange,
    handleShopChange,
    handleColumnsChange,
    handleClearFilters,
    refetch,
    isFetchingNextPage,
    hasActiveFilters,
  } = useRefundsMain();

  const { tanStackColumns } = useAgGridColumns({ selectedColumns });
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  if (isInitialLoad && isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error as Error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              searchTerm={searchTerm}
              onSearch={handleSearch}
              shopId={shopIdParam}
              onShopChange={handleShopChange}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateRangeChange={handleDateRangeChange}
              onClearFilters={handleClearFilters}
              allColumns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
        </div>

        {!isLoading && (
          <RefundsStatusToolbar
            activeStatus={statusFilter ?? ''}
            onStatusChange={handleStatusChange}
            statusCounts={statusCounts}
            isLoadingCounts={isLoadingStatusCounts}
          />
        )}
      </div>

      <div
        className={`transition-all duration-300 mb-4 ${
          orders.length > 0 || hasActiveFilters ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden'
        } ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {isMobile ? (
          <RefundCardList
            orders={ordersViewProps.orders}
            fetchNextPage={ordersViewProps.fetchNextPage}
            hasNextPage={ordersViewProps.hasNextPage}
            isFetchingNextPage={ordersViewProps.isFetchingNextPage}
            selectedColumns={ordersViewProps.selectedColumns}
          />
        ) : (
          <RefundsAgGridTable {...ordersViewProps} />
        )}
      </div>

      {orders.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.DEFAULT} />
      )}
    </div>
  );
};
